import { Card, CardContent, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { signInSession, getUserData, getOwnMalls } from '@/services/auth.service';
import { getSpaceUserData } from '@/services/studio.service';
import { dialogClose } from '@/modules/MuiDialog';
import sessionStore from '@/store/modules/sessionStore';
import spaceUserV2Store from '@/store/modules/spaceUserV2Store';
import AuthForm from '@/components/auth/AuthForm';

interface LoginDialogProps {
    onLoginSuccess?: () => void;
}

/**
 * 로그인 모달 — 페이지 이동 없이 로그인시킨다.
 * 폼 본문은 `components/auth/AuthForm` 을 그대로 쓰므로 로그인 페이지와 구성·순서가 같다.
 */
export default function LoginDialog({ onLoginSuccess }: LoginDialogProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // 모달은 페이지 이동 없이 그 자리에서 이어가므로 현재 경로를 소셜 state 로 넘긴다
    const currentPath = window.location.pathname;

    const signIn = async (email: string, password: string) => {
        try {
            // 1. signInSession 내부에서 setSession을 호출하여 sessionStore에 저장
            const session = await signInSession(email, password);

            // 2. 추가 사용자 데이터 로드 (userStore, spaceUserStore, spaceUserV2Store 업데이트)
            try {
                await Promise.all([getUserData(session.userId), getSpaceUserData(session.userId)]);
            } catch (err) {
                console.warn('사용자 추가 정보 로드 실패:', err);
            }

            // 3. 몰 정보 로드 시도 (몰이 있는 경우 mallId 업데이트)
            try {
                const { persistenceMallId } = await getOwnMalls(session.accessToken);
                if (persistenceMallId) {
                    const { setSession } = sessionStore.getState();
                    setSession({ ...session, mallId: persistenceMallId });
                }
            } catch (err) {
                console.warn('몰 정보 로드 실패 (몰이 개설되지 않았을 수 있음):', err);
            }

            toast.success('로그인되었습니다.');

            // 다이얼로그 닫기 - sessionStore가 업데이트되면 자동으로 UI가 반영됨
            dialogClose();

            // [기획 변경 2026-09] 로그인 자동 전파(eager) 폐지 — 연동은 수동 "가져오기"로만

            // 4. 소도몰 역할 확인 및 리다이렉트
            const { grade_name } = spaceUserV2Store.getState();
            const isSodomall = grade_name === '소도몰';

            if (onLoginSuccess) {
                onLoginSuccess();
            } else if (isSodomall) {
                navigate(`/studio/${session.userId}`);
            }
        } catch (e: any) {
            console.error(e);

            if (!!e.data?.placeHolder || !!e.placeHolder) {
                toast.error(e.data?.placeHolder || e.placeHolder);
                return;
            }
            if (e.status === 302) {
                toast.error('비활성화 된 계정입니다.');
                return;
            }
            toast.error('로그인이 실패하였습니다.');
        }
    };

    return (
        <Card
            elevation={0}
            sx={{
                width: '100%',
                maxWidth: 420,
                borderRadius: '20px',
                position: 'relative',
            }}
        >
            <IconButton
                aria-label={t('common.close')}
                onClick={() => dialogClose()}
                sx={{ position: 'absolute', right: 8, top: 8, cursor: 'pointer' }}
            >
                <CloseIcon />
            </IconButton>

            <CardContent sx={{ display: 'flex', justifyContent: 'center', px: 3, pt: 5, pb: 4 }}>
                <AuthForm
                    refer={currentPath}
                    onSubmitCredentials={signIn}
                    dense
                    showMobileBrand={false}
                />
            </CardContent>
        </Card>
    );
}
